import React from 'react';
import type { TableColumnsType } from 'antd';
import { Table, Tag } from 'antd';
import { TransactionFlowRecordType } from '../graphql/types/TransactionFlowRecordType';
import { formatTimeWithTimezone, normalizeUtcString } from '../../../../../utils/timezoneFormatter';

interface TransactionFlowRecordRowProps {
    rowData: TransactionFlowRecordType[];
    loading?: boolean;
    /** 展示"交易时间"所用的 IANA 时区，与筛选器所选时区联动，未选时区时传 'UTC' */
    timeZoneId: string;
}

// 交易类型颜色映射
const transactionTypeColorMap: Record<number, { color: string; label: string }> = {
    1: { color: 'orange', label: '代扣' },
    2: { color: 'blue', label: '调账' },
    3: { color: 'cyan', label: '退款' },
    7: { color: 'purple', label: '调账-赔付' },
};

// 交易状态颜色映射
const transactionResultColorMap: Record<string, { color: string; label: string }> = {
    'SUCCESS': { color: 'success', label: '成功' },
    'FAILED': { color: 'error', label: '失败' },
    'PROCESSING': { color: 'processing', label: '处理中' },
    'CANCELLED': { color: 'default', label: '已取消' },
};

export const TransactionFlowRecordColumns: React.FC<TransactionFlowRecordRowProps> = ({
    rowData,
    loading,
    timeZoneId
}) => {

    const transactionFlowRecordColumns: TableColumnsType<TransactionFlowRecordType> = [
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            align: 'center',
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            align: 'center',
        },
        {
            title: '交易类型',
            key: 'transactionType',
            align: 'center',
            render: (_, record) => {
                const typeConfig = transactionTypeColorMap[record.transactionType];
                if (typeConfig) {
                    return <Tag color={typeConfig.color}>{typeConfig.label}</Tag>;
                }
                return <Tag>{record.transactionTypeDescription}</Tag>;
            },
        },
        {
            title: '业务订单号',
            dataIndex: 'bizOrderNo',
            key: 'bizOrderNo',
            align: 'center',
            render: (bizOrderNo) => bizOrderNo || '-',
        },
        {
            title: '结算交易号',
            dataIndex: 'businessId',
            key: 'businessId',
            align: 'center',
        },
        {
            title: '交易状态',
            dataIndex: 'transactionResult',
            key: 'transactionResult',
            align: 'center',
            render: (transactionResult) => {
                const statusConfig = transactionResultColorMap[transactionResult];
                if (statusConfig) {
                    return <Tag color={statusConfig.color}>{statusConfig.label}</Tag>;
                }
                return <Tag>{transactionResult}</Tag>;
            },
        },
        {
            title: '结算交易ID',
            dataIndex: 'transactionNo',
            key: 'transactionNo',
            align: 'center',
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            align: 'center',
            render: (currency) => {
                const currencyMap: Record<string, string> = {
                    'USD': '美元',
                    'CNY': '人民币'
                };
                return currencyMap[currency] || currency;
            },
        },
        {
            title: '金额',
            key: 'transactionAmount',
            align: 'center',
            render: (_, record) => {
                return record.transactionAmount ? `$${record.transactionAmount}` : '$0.00';
            },
        },
        {
            title: '交易时间',
            dataIndex: 'transactionTime',
            key: 'transactionTime',
            align: 'center',
            render: (transactionTime: string) => formatTimeWithTimezone(normalizeUtcString(transactionTime), timeZoneId),
        }
    ];

    return (
        <Table
            rowKey={(record) => record.id}
            columns={transactionFlowRecordColumns}
            dataSource={rowData}
            scroll={{ x: 1950 }}
            size="middle"
            className="mb-4"
            pagination={false}
            loading={loading}
        />
    );
};




